
import { useNavigate } from "react-router-dom";
import { IGarantie, IOptionCotisation, IRERequestDataOffers } from "../../context/model";
import StickButtonsBloc from "../common/StickButtonsBloc";
import useAppState from "../../context/AppContext";
import { APP_RETIREMENT_URLS, ERROR_MESSAGES } from "../../const/AppConst";
import REGuarantyOfferFormComponent from "../../components/retirement/guaranty/REGuarantyOfferFormComponent";
import REGuarantyCompareFormComponent from "../../components/retirement/guaranty/REGuarantyCompareFormComponent";
import { useEffect, useState } from "react";
import { calculOffersRetired } from "../../utils/dataUtils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { getInfoRetirementSteps } from "../../utils/utils";
import REGuarantyInfoFormComponent from "../../components/retirement/guaranty/REGuarantyInfoFormComponent";
import REGuarantyTableInfoComponent from "../../components/retirement/guaranty/REGuarantyTableInfoComponent";

type GuarantyOfferBlocProps = {
    handleSwitchPage: (pageCode: "INFO" | "INFO_TABLE" | "OFFER" | "COMPARE") => void;
    garanties: IGarantie[];
    displayedPage: "INFO" | "INFO_TABLE" | "OFFER" | "COMPARE"
}

type  REGuarantyRetirementBlocFormData = {
  offerCode: string,
  hasReduction: string | null
}

export default function REGuarantyRetirementBloc({handleSwitchPage, garanties, displayedPage } : Readonly<GuarantyOfferBlocProps>) {

  const navigate = useNavigate();
  const {
    state: { jwtToken, updatedProposition, additionalData, infoSteps, proposition, hasReduction },
    dispatch
  } = useAppState();

  const [offers, setOffers] = useState<IOptionCotisation[]>([]);
  const [errMsg, setErrMsg] = useState<string>("");

  const validationSchema = Yup.object().shape({
    offerCode: Yup.string().required(ERROR_MESSAGES.requiredMsg),
    hasReduction: Yup.string().oneOf(['true', 'false']).required(ERROR_MESSAGES.requiredMsg),
  });

  const {
    register,
    handleSubmit, 
    setValue, 
    watch,
    formState: { errors },
  } = useForm<REGuarantyRetirementBlocFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      offerCode : updatedProposition?.offerCode ?? "",
      hasReduction: hasReduction !== null ? hasReduction?.toString() : null,
    },
    resolver: yupResolver(validationSchema),
  });


  useEffect(() => {
    const fetchTotal = async () => {
      if (jwtToken) {
        const token = jwtToken.accessToken;
        const body = {
          principalMemberInfo: {
            retiredDate: updatedProposition?.retirementDate,
            birthDate: updatedProposition?.birthDate,
            hasAlsaceMoselle: updatedProposition?.alsaceMoselleRegime
          },
          partnerInfo: additionalData?.hasPartner ? additionalData?.partnerInfo : null,
          childrenInfo: additionalData?.children,
          effectiveDate: updatedProposition?.effectiveDate,
          coveragesInfo: updatedProposition?.coveragesInfo
        } as IRERequestDataOffers;
        
        const result = await calculOffersRetired(token, body);
        setOffers(result);
      }
    } 

    fetchTotal();
  }, []);

  const handleGetDisplayedPage = (mode: "PREVIOUS" | "NEXT") => {
    const pages = ["INFO", "INFO_TABLE", "OFFER", "COMPARE"];
    const currentPageIndex = pages.indexOf(displayedPage);
    if (mode === "PREVIOUS") {
      const previousSteps = pages[currentPageIndex - 1];
      return previousSteps;
    } else {
      const nextSteps = pages[currentPageIndex + 1];
      return nextSteps;
    }

  }

  const onNextAction = async (data: REGuarantyRetirementBlocFormData) => {
    let tempUpdatedPropo = updatedProposition;
    if(tempUpdatedPropo) {
      tempUpdatedPropo.offerCode = data.offerCode;
    }
      const hasDocFamilyStep = getInfoRetirementSteps
      (infoSteps, "INFO_FAMILY_DOC", 
        (additionalData?.hasChild || additionalData?.hasPartner || hasReduction) ??false,
        "INFO_LASTEMPLOYEE_DOC"
      );
  
      if (hasDocFamilyStep.hasChanged) {
        dispatch({type: "UPDATE_STEPS", 
          steps: hasDocFamilyStep.infoSteps
        })
      }
    
      if (displayedPage !== "COMPARE") {
        const nextStep = handleGetDisplayedPage("NEXT");
          handleSwitchPage(nextStep as "INFO" | "INFO_TABLE" | "OFFER" | "COMPARE");
      } else {
          const nextUrl = APP_RETIREMENT_URLS.infoIdentityPage
    
          dispatch({type: 'NEXT_INFO_STEP', path: nextUrl, updatedProposition: tempUpdatedPropo});
          navigate({
              pathname: nextUrl,
              search: `?id=${proposition!.propositionId}`,
          });  
      }       
  }

  const handleNextStep = () => {
    console.log('hasReduction', hasReduction)
    if (displayedPage === "INFO_TABLE" && hasReduction === null) {
      setErrMsg(ERROR_MESSAGES.requiredMsg)
      return;
    }

    const nextStep = handleGetDisplayedPage("NEXT");
    handleSwitchPage(nextStep as "INFO" | "INFO_TABLE" | "OFFER" | "COMPARE");
  }


  const onPreviousAction = async () => {
    if (displayedPage === "INFO") {
      const url = APP_RETIREMENT_URLS.besoinPage
    
      dispatch({type: 'BACK_INFO_STEP', path: url});
      navigate({
          pathname: url,
          search: `?id=${proposition!.propositionId}`,
      });   
    } else {
      const previousStep = handleGetDisplayedPage("PREVIOUS");
      handleSwitchPage(previousStep as "INFO" | "INFO_TABLE" | "OFFER" | "COMPARE");
    }
          
  }

  const handleChooseOffer = (e: any, option: string) => { 
    e.preventDefault();
    setValue('offerCode' , option, {shouldValidate : true, });
  };

  const handleSetHasReduction = (response: boolean) => {
    setErrMsg('');
    dispatch({type: "SET_HAS_REDUCTION", 
        hasReduction: response
      })
  }


  return (
    <div className="">
      <div className="">
        {
          displayedPage === "INFO" &&
          <REGuarantyInfoFormComponent />
        }
        {
          displayedPage === "INFO_TABLE" &&
          <REGuarantyTableInfoComponent
            handleSetHasReduction={handleSetHasReduction} 
            register={register}
            errors={errors}
          />
        }
        {
          displayedPage === "OFFER" &&
          <REGuarantyOfferFormComponent 
            garanties={garanties} 
            handleSwitchPage={handleSwitchPage} 
            offers={offers} 
            offerCode={watch('offerCode')} 
            errors={errors} 
            handleChooseOffer={handleChooseOffer}/>
        }
        {
          displayedPage === "COMPARE" &&
          <REGuarantyCompareFormComponent  
            garanties={garanties} 
            handleSwitchPage={handleSwitchPage} 
            offers={offers} 
            offerCode={watch('offerCode')} 
            errors={errors} 
            handleChooseOffer={handleChooseOffer} 
            register={register}/> 
        } 
      </div>

      <div className=''>
        { errMsg && displayedPage === "INFO_TABLE" && (
            <p className="error fushia m-y-xs">Veuillez choisir au moins une case</p>
        )}
      </div>  

      {
        displayedPage === "INFO" || displayedPage === "INFO_TABLE" ?
          <StickButtonsBloc 
            showPreviousButton={true} 
            disabledPreviousButton={false} 
            nextAction={handleNextStep} 
            showNextButton={true} 
            previousAction={onPreviousAction}
          />
          :
          <StickButtonsBloc 
            showPreviousButton={true} 
            disabledPreviousButton={false} 
            nextAction={handleSubmit(onNextAction)} 
            showNextButton={true} 
            previousAction={onPreviousAction}
          />
      } 
    </div>
  )
}